import { Flex, Title } from '@mantine/core';
import {
  VacancyHeader,
  VacancyMainInfoCard,
  VacancyActivityBlock,
  VacancyEditMainInfo,
  VacancyEditMainInfoFormRoot,
  VacancyEditMainInfoTariffField,
  type VacancyEditMainInfoInitialValues,
} from '@features/vacancy';
import { SelectedCandidates } from '@widgets/candidate';
import { TariffWidget } from '@widgets/tariff';
import { useMemo, useState } from 'react';
import type { Vacancy } from '@entities/vacancy';
import { formatActivitySpheresList } from '@entities/activitySphere';
import { useShowMe } from '@entities/auth';
import { getCityNames } from '@entities/region';

type VacancyInfoProps = {
  vacancy: Vacancy;
  vacancyId: number;
};

export const VacancyInfo = ({ vacancy, vacancyId }: VacancyInfoProps) => {
  const [isEditMainInfo, setIsEditMainInfo] = useState(false);

  const { data: me } = useShowMe();

  const editMainInfoInitialValues = useMemo<VacancyEditMainInfoInitialValues>(
    () => ({
      name: vacancy?.name || '',
      companyId: vacancy?.company.id.toString() || '',
      cityIds: vacancy.cities?.map(city => city.id.toString()) ?? [],
      requiredCandidatesCount:
        vacancy?.requiredCandidatesCount.toString() || '',
      activitySphereIds: vacancy?.activitySpheres.map(sphere =>
        sphere.id.toString()
      ),
      ai_sphere_fill: vacancy?.ai_sphere_fill || false,
      scheduleWorkIds:
        vacancy?.scheduleWorks.map(({ id }) => id.toString()) || [],
      employmentTypeIds:
        vacancy?.employmentTypes.map(({ id }) => id.toString()) || [],
      skills: vacancy?.skills.map(({ name }) => name) || [],
      currencyId: vacancy?.currency.id.toString() || '',
      salaryFrom: vacancy?.salaryFrom || 0,
      salaryTo: vacancy?.salaryTo || 0,
      paymentPeriodId: vacancy?.paymentPeriod?.id.toString() || '',
      mandatoryRequirements: vacancy?.mandatoryRequirements || '',
      additionalRequirements: vacancy?.additionalRequirements || '',
      interviewFinalMessage: vacancy?.interviewFinalMessage ?? '',
      responsibilities: vacancy?.responsibilities || '',
      status: vacancy?.status || '',
      tariffId: String(vacancy.tariffs.id),
    }),
    [vacancy]
  );

  const handleEditMainInfo = () => {
    setIsEditMainInfo(true);
  };

  const handleCancelEditMainInfo = () => {
    setIsEditMainInfo(false);
  };

  const mainRow = isEditMainInfo ? (
    <VacancyEditMainInfoFormRoot initialValues={editMainInfoInitialValues}>
      <Flex wrap='wrap' gap={24} align='flex-start'>
        <Flex direction='column' gap={24} flex={{ base: 'auto', md: '1' }}>
          <VacancyEditMainInfo
            handleCancel={handleCancelEditMainInfo}
            initialValues={editMainInfoInitialValues}
          />
        </Flex>

        <Flex direction='column' gap={24}>
          <VacancyActivityBlock
            vacancyId={vacancyId?.toString() || ''}
            responsibleUserId={String(vacancy.responsibleUser.id)}
            isMe={me?.id === vacancy.responsibleUser.id}
            responsibleUserName={vacancy.responsibleUser.profile.firstName}
            responsibleUserLastName={vacancy.responsibleUser.profile.lastName}
            responsibleUserRole={vacancy.responsibleUser.role.name}
            status={vacancy?.status || ''}
            activeFrom={vacancy?.vacancyActivities[0]?.activeFrom}
            activeTo={vacancy?.vacancyActivities[0]?.activeTo}
            statusesHistory={vacancy?.statusesHistory || []}
          />

          <VacancyEditMainInfoTariffField />

          <SelectedCandidates
            vacancyId={vacancy.id}
            vacancyName={vacancy.name}
            vacantPlaces={vacancy.requiredCandidatesCount}
            currency={vacancy.currency.value}
            paymentPeriod={vacancy.paymentPeriod.name}
          />
        </Flex>
      </Flex>
    </VacancyEditMainInfoFormRoot>
  ) : (
    <Flex wrap='wrap' gap={24} align='flex-start'>
      <Flex direction='column' gap={24} flex={{ base: 'auto', md: '1' }}>
        <VacancyMainInfoCard
          name={vacancy.name}
          companyName={vacancy.company?.name || ''}
          cityNames={getCityNames(vacancy)}
          requiredCandidatesCount={vacancy.requiredCandidatesCount}
          handleEdit={handleEditMainInfo}
          activitySpheres={formatActivitySpheresList(
            vacancy?.activitySpheres ?? []
          )}
          scheduleWorks={
            Array.isArray(vacancy?.scheduleWorks)
              ? vacancy.scheduleWorks.map(schedule => schedule.name).join(', ')
              : ''
          }
          employmentTypes={
            Array.isArray(vacancy?.employmentTypes)
              ? vacancy.employmentTypes
                  .map(employmentType => employmentType.name)
                  .join(', ')
              : ''
          }
          skills={
            Array.isArray(vacancy?.skills)
              ? vacancy.skills.map(skill => skill.name).join(', ')
              : ''
          }
          salaryFrom={vacancy?.salaryFrom || 0}
          salaryTo={vacancy?.salaryTo || 0}
          currencyValue={vacancy?.currency?.value || ''}
          paymentPeriod={vacancy?.paymentPeriod?.name || ''}
          mandatoryRequirements={vacancy?.mandatoryRequirements}
          additionalRequirements={vacancy?.additionalRequirements}
          responsibilities={vacancy?.responsibilities}
          interviewFinalMessage={vacancy?.interviewFinalMessage || ''}
        />
      </Flex>

      <Flex direction='column' gap={24}>
        <VacancyActivityBlock
          vacancyId={vacancyId?.toString() || ''}
          responsibleUserId={String(vacancy.responsibleUser.id)}
          isMe={me?.id === vacancy.responsibleUser.id}
          responsibleUserName={vacancy.responsibleUser.profile.firstName}
          responsibleUserLastName={vacancy.responsibleUser.profile.lastName}
          responsibleUserRole={vacancy.responsibleUser.role.name}
          status={vacancy?.status || ''}
          activeFrom={vacancy?.vacancyActivities[0]?.activeFrom}
          activeTo={vacancy?.vacancyActivities[0]?.activeTo}
          statusesHistory={vacancy?.statusesHistory || []}
        />

        <TariffWidget vacancyId={vacancyId} tariff={vacancy.tariffs} />

        <SelectedCandidates
          vacancyId={vacancy.id}
          vacancyName={vacancy.name}
          vacantPlaces={vacancy.requiredCandidatesCount}
          currency={vacancy.currency.value}
          paymentPeriod={vacancy.paymentPeriod.name}
        />
      </Flex>
    </Flex>
  );

  return (
    <Flex
      wrap='wrap'
      direction='column'
      gap={24}
      pt={16}
      mx='auto'
      maw={1512}
      w='100%'
    >
      <VacancyHeader
        id={vacancy.id}
        createdAt={vacancy.createdAt}
        updatedAt={vacancy.updatedAt}
      />

      <Title order={3} c='primary.7'>
        {vacancy.name}
      </Title>

      {mainRow}
    </Flex>
  );
};
